import {
  Appointment,
  APPOINTMENT_STATUS,
  APPOINTMENT_CHANNEL,
  MODES_OF_APT,
} from "@elixir/types";
import { PARCHI_HOST } from "../../../constants/env";
import { OfflineAppointment } from "../../../firestore/types";

export function getAppointmentAttributes(
  item: Pick<
    OfflineAppointment,
    | "channel"
    | "callback_requested"
    | "status"
    | "prescription_id"
    | "prescription_url"
    | "payment_id"
    | "payment_status"
    | "call_status"
    | "mode"
    | "visit_start"
    | "clinic_id"
    | "receipts"
  >
): Pick<
  Appointment,
  | "stage"
  | "visit-type"
  | "type-display"
  | "prescription_url"
  | "rc"
  | "can_abort"
  | "can_pay"
  | "receipts"
  | "billStatus"
  | "billTotal"
> {
  let stage = "";
  const {
    channel,
    callback_requested,
    status,
    prescription_id,
    payment_status,
    call_status,
    prescription_url,
    mode,
    visit_start,
    clinic_id,
  } = item;
  if (
    status === APPOINTMENT_STATUS.COMPLETED ||
    status === APPOINTMENT_STATUS.COMPLETED_NO_PRESCRIPTION ||
    !!prescription_id
  ) {
    stage = "COMPLETED";
  } else if (status === APPOINTMENT_STATUS.PARKED) {
    stage = "PARKED";
  } else if (visit_start) {
    stage = "ONGOING";
  }

  let rc = false;

  if (
    callback_requested &&
    (status === APPOINTMENT_STATUS.BOOKED ||
      status === APPOINTMENT_STATUS.ONGOING ||
      status === APPOINTMENT_STATUS.PARKED ||
      status === APPOINTMENT_STATUS.CHECKED_IN)
  ) {
    if (
      new Date().getTime() <
      new Date(callback_requested).getTime() + 1000 * 60 * 60
    ) {
      rc = true; // request callback
    }
  }

  let type: Appointment["visit-type"] =
    mode === "tele" ? "TELE" : "APPOINTMENT";
  let display = "Appointment";
  if (channel === APPOINTMENT_CHANNEL.SPHERE_INDIA_COVID) {
    type = "TELE";
    display = "Sphere India Covid";
  } else if (channel === APPOINTMENT_CHANNEL.EMERGENCY) {
    type = "TELE";
    display = "Emergency";
  } else if (channel === APPOINTMENT_CHANNEL.SECOND_OPINION) {
    type = "TELE";
    display = "Second Opinion";
  } else if (channel === APPOINTMENT_CHANNEL.TALK_TO_DOCTOR) {
    type = "TELE";
    display = "Talk To Doctor";
  } else if (channel === APPOINTMENT_CHANNEL.SPHERE_INDIA_PSYCHOLOGY) {
    type = "TELE";
    display = "Sphere India Psyc";
  } else if (channel === APPOINTMENT_CHANNEL.CORPORATE_CONNECT) {
    type = "TELE";
    display = "Corporate Connect";
  } else if (channel === APPOINTMENT_CHANNEL.COVID_CONNECT) {
    type = "TELE";
    display = "Covid Connect";
  } else if (clinic_id === "TELE" || mode === MODES_OF_APT.TELE) {
    type = "TELE";
    display = "Tele";
  } else if (
    channel === APPOINTMENT_CHANNEL.OFFLINE ||
    channel === APPOINTMENT_CHANNEL.WALKIN ||
    channel === APPOINTMENT_CHANNEL.ON_PAD ||
    channel === APPOINTMENT_CHANNEL.WALKINQR
  ) {
    type = "WALKIN";
    display = "Walk-in";
  }
  const p_url =
    prescription_url ||
    (prescription_id ? `${PARCHI_HOST}/${prescription_id}.pdf` : undefined);

  const isPrepaid = !!payment_status;

  const isPaymentRequestedForReceipts =
    item?.receipts?.reduce((prev, curr) => {
      if (curr.payment_id) {
        return true;
      }

      return prev;
    }, false) || false;

  const billingStatus: Appointment["billStatus"] = item.receipts.reduce<
    Appointment["billStatus"]
  >(
    (prev, curr) =>
      getAptBillStatusForQueue({
        billValueSoFar: prev,
        receipt: curr,
      }),
    item.payment_status === "success" ? "paid" : "unbilled"
  );

  const billAmount = item.receipts.reduce((prev, curr) => {
    if (
      curr.net_amount &&
      (!curr.payment_id ||
        paymentStatusMap.receipt_generated ===
          paymentStatusMap[curr.payment_status])
    ) {
      return prev + curr.net_amount;
    }

    return prev;
  }, 0);

  return {
    stage,
    "visit-type": type,
    "type-display": display,
    rc,
    can_abort:
      (status === APPOINTMENT_STATUS.ONGOING ||
        status === APPOINTMENT_STATUS.PARKED) &&
      call_status === "connected",
    can_pay: !isPrepaid && !isPaymentRequestedForReceipts,
    prescription_url: p_url,
    receipts: item.receipts.map((item) => ({
      ...item,
      payment_status: item.payment_status || "success",
    })),
    billStatus: billingStatus,
    billTotal: billAmount,
  };
}

export const getAptBillStatusForQueue = ({
  billValueSoFar,
  receipt,
}: {
  billValueSoFar: string;
  receipt: Appointment["receipts"][0];
}): Appointment["billStatus"] => {
  if (
    billValueSoFar === paymentStatusMap.receipt_generated ||
    !receipt.payment_status ||
    paymentStatusMap[receipt.payment_status] ===
      paymentStatusMap.receipt_generated
  ) {
    return paymentStatusMap.receipt_generated;
  }

  return paymentStatusMap[receipt.payment_status];
};

export const paymentStatusMap: Record<string, Appointment["billStatus"]> = {
  created: "pending",
  payment_initiated: "pending",
  cancelled: "pending",

  payment_success: "paid",
  payment_processing: "paid",
  success: "paid",
  settled: "paid",

  unbilled: "unbilled",

  receipt_generated: "paid",

  refunded: "refunded",
  refund_processed: "refunded",
  to_refund: "refunded",

  payment_failed: "failed",
  failed: "failed",
};
